import { Sfx } from './audio.js';
import { FEEL } from './feel.js';

const canvas = document.getElementById('game');
const ctx = canvas.getContext('2d', { alpha: false });
const overlay = document.getElementById('overlay');
const finalScoreEl = document.getElementById('final-score');
const bestLineEl = document.getElementById('best-line');
const retryBtn = document.getElementById('retry');
const hintEl = document.getElementById('hint');

const WUXING = [
  { id: 'jin',  char: '金', fill: '#e8eef5', glow: '#c0d0e8', rim: '#9aa8bc' },
  { id: 'mu',   char: '木', fill: '#2ecc71', glow: '#57e092', rim: '#1a8f4a' },
  { id: 'shui', char: '水', fill: '#2a6fd6', glow: '#5aa0ff', rim: '#1a3f8a' },
  { id: 'huo',  char: '火', fill: '#e74c3c', glow: '#ff6b4a', rim: '#9b1c12' },
  { id: 'tu',   char: '土', fill: '#c9a227', glow: '#e0c060', rim: '#8a6b14' },
];

const BEST_KEY = 'wuxing-ring-best';

/** @type {ReturnType<typeof freshState>} */
let G;
let W = 390, H = 844, DPR = 1;
let CX = 195, CY = 400, R = 160;
let lastT = 0;
let shakeT = 0;
let particles = [];
let floats = [];
let stars = [];
let mountains = [];
let mistPhase = 0;
let ready = false;

function freshState() {
  return {
    phase: 'playing', // playing | dead
    angle: -Math.PI / 2, // player at top initially? bottom looks better for thumb — use bottom
    score: 0,
    streak: 0,
    bullets: [],   // {x,y,vx,vy,hostile,life}
    orbs: [],      // {angle, radius, kind, pulse, hp}
    fireCd: 0,
    aliveMs: 0,
    spawnCd: 400,
    deathAt: 0,
  };
}

function resize() {
  DPR = Math.min(window.devicePixelRatio || 1, 2.5);
  W = window.innerWidth;
  H = window.innerHeight;
  canvas.width = Math.floor(W * DPR);
  canvas.height = Math.floor(H * DPR);
  canvas.style.width = W + 'px';
  canvas.style.height = H + 'px';
  ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
  CX = W * 0.5;
  CY = H * 0.46;
  R = Math.min(W, H) * 0.36;
  buildDecor();
}

function buildDecor() {
  stars = [];
  for (let i = 0; i < 90; i++) {
    stars.push({
      x: Math.random() * W,
      y: Math.random() * H * 0.72,
      r: 0.4 + Math.random() * 1.4,
      a: 0.15 + Math.random() * 0.55,
      tw: Math.random() * Math.PI * 2,
    });
  }
  mountains = [];
  // far ridge
  mountains.push(makeRidge(H * 0.55, H * 0.22, 0.18, 7));
  mountains.push(makeRidge(H * 0.68, H * 0.28, 0.32, 5));
  mountains.push(makeRidge(H * 0.82, H * 0.26, 0.55, 4));
}

function makeRidge(baseY, amp, alpha, peaks) {
  const pts = [];
  const n = 28;
  for (let i = 0; i <= n; i++) {
    const x = (i / n) * W;
    let y = baseY;
    for (let k = 1; k <= peaks; k++) {
      y -= Math.sin(i * 0.4 + k * 1.7) * (amp / k) * (0.5 + 0.5 * Math.sin(k * 2.1));
    }
    pts.push({ x, y });
  }
  return { pts, alpha, baseY };
}

function randOrb() {
  const kind = (Math.random() * WUXING.length) | 0;
  // place away from player angularly a bit
  let a = Math.random() * Math.PI * 2;
  const pr = R * (0.38 + Math.random() * 0.38);
  return { angle: a, radius: pr, kind, pulse: Math.random() * Math.PI * 2, hitFlash: 0 };
}

function reset() {
  G = freshState();
  G.angle = Math.PI / 2; // bottom of ring — thumb friendly
  G.orbs = [];
  for (let i = 0; i < 4; i++) G.orbs.push(randOrb());
  particles = [];
  floats = [];
  overlay.classList.remove('show');
  hintEl.style.opacity = '0.7';
  setTimeout(() => { if (G.phase === 'playing') hintEl.style.opacity = '0'; }, 3200);
}

function playerPos() {
  const r = R - FEEL.ringThickness * 0.55;
  return {
    x: CX + Math.cos(G.angle) * r,
    y: CY + Math.sin(G.angle) * r,
    r,
  };
}

function shoot() {
  if (G.phase !== 'playing') return false;
  if (G.fireCd > 0) return false;
  G.fireCd = FEEL.shootCooldownMs / 1000;
  const p = playerPos();
  // inward toward center
  const dx = CX - p.x, dy = CY - p.y;
  const len = Math.hypot(dx, dy) || 1;
  const sp = FEEL.bulletSpeed * (R / 160);
  G.bullets.push({
    x: p.x + (dx / len) * 10,
    y: p.y + (dy / len) * 10,
    vx: (dx / len) * sp,
    vy: (dy / len) * sp,
    hostile: false,
    life: 4,
  });
  Sfx.play('shoot', { k: 1 });
  return true;
}

function kill() {
  if (G.phase !== 'playing') return;
  G.phase = 'dead';
  G.deathAt = performance.now();
  Sfx.play('fail');
  shakeT = FEEL.hitShakeMs / 1000;
  const best = Math.max(G.score, Number(localStorage.getItem(BEST_KEY) || 0));
  localStorage.setItem(BEST_KEY, String(best));
  finalScoreEl.textContent = `得分 ${G.score}`;
  bestLineEl.textContent = best > G.score ? `最高 ${best}` : (G.score > 0 ? '新纪录' : '');
  setTimeout(() => overlay.classList.add('show'), FEEL.deathFreezeMs);
}

function addFloat(x, y, text, color) {
  floats.push({ x, y, text, color, t: 0, life: FEEL.floatScoreMs / 1000 });
}

function burst(x, y, color, n = 14) {
  for (let i = 0; i < n; i++) {
    const a = Math.random() * Math.PI * 2;
    const s = 40 + Math.random() * 140;
    particles.push({
      x, y, vx: Math.cos(a) * s, vy: Math.sin(a) * s,
      life: 0.35 + Math.random() * 0.35, t: 0, color, r: 1.5 + Math.random() * 2.5,
    });
  }
}

function update(dt) {
  mistPhase += dt;
  if (shakeT > 0) shakeT = Math.max(0, shakeT - dt);

  for (const p of particles) { p.t += dt; p.x += p.vx * dt; p.y += p.vy * dt; p.vx *= 0.96; p.vy *= 0.96; }
  particles = particles.filter((p) => p.t < p.life);
  for (const f of floats) { f.t += dt; f.y -= 36 * dt; }
  floats = floats.filter((f) => f.t < f.life);

  if (G.phase !== 'playing') return;

  G.aliveMs += dt * 1000;
  G.fireCd = Math.max(0, G.fireCd - dt);
  G.spawnCd -= dt * 1000;
  if (G.spawnCd <= 0 && G.orbs.length < FEEL.maxOrbs) {
    G.orbs.push(randOrb());
    G.spawnCd = FEEL.spawnIntervalMs * (0.75 + Math.random() * 0.5);
  }

  // slow orbit drift on orbs
  for (const o of G.orbs) {
    o.angle += dt * 0.12 * (o.kind % 2 === 0 ? 1 : -1);
    o.pulse += dt * 2.4;
    if (o.hitFlash > 0) o.hitFlash -= dt;
  }

  // bullets
  const nearR = FEEL.nearCenterR * (R / 160);
  for (const b of G.bullets) {
    b.x += b.vx * dt;
    b.y += b.vy * dt;
    b.life -= dt;
    const dx = b.x - CX, dy = b.y - CY;
    const dist = Math.hypot(dx, dy);

    if (!b.hostile) {
      // reverse near center
      if (dist < nearR) {
        const len = dist || 1;
        const sp = FEEL.hostileSpeed * (R / 160);
        b.vx = (dx / len) * sp;
        b.vy = (dy / len) * sp;
        b.hostile = true;
        b.life = 3.5;
        Sfx.play('reject');
        burst(b.x, b.y, '#ff8866', 8);
      }
    } else {
      // hostile leaving ring → remove
      if (dist > R + 40) b.life = 0;
    }
  }

  // bullet vs orbs (friendly only)
  for (const b of G.bullets) {
    if (b.hostile || b.life <= 0) continue;
    for (let i = G.orbs.length - 1; i >= 0; i--) {
      const o = G.orbs[i];
      const ox = CX + Math.cos(o.angle) * o.radius;
      const oy = CY + Math.sin(o.angle) * o.radius;
      const or = FEEL.orbRadius * (R / 160);
      if (Math.hypot(b.x - ox, b.y - oy) < or + 6) {
        b.life = 0;
        const wx = WUXING[o.kind];
        G.streak += 1;
        const pts = FEEL.scoreBase + (G.streak - 1) * FEEL.streakBonus;
        G.score += pts;
        addFloat(ox, oy - 10, `+${pts}`, wx.glow);
        burst(ox, oy, wx.glow, 18);
        Sfx.play('pop', { ci: o.kind, streak: G.streak });
        G.orbs.splice(i, 1);
        G.spawnCd = Math.min(G.spawnCd, 500);
        break;
      }
    }
  }

  G.bullets = G.bullets.filter((b) => b.life > 0);

  // collisions with player
  const p = playerPos();
  const pr = FEEL.playerSize * 0.55;
  for (const b of G.bullets) {
    if (!b.hostile) continue;
    if (Math.hypot(b.x - p.x, b.y - p.y) < pr + 5) {
      kill();
      return;
    }
  }
  for (const o of G.orbs) {
    const ox = CX + Math.cos(o.angle) * o.radius;
    const oy = CY + Math.sin(o.angle) * o.radius;
    const or = FEEL.orbRadius * (R / 160);
    // only dangerous if orb drifts near ring edge close to player
    if (Math.hypot(ox - p.x, oy - p.y) < or + pr) {
      kill();
      return;
    }
  }
}

/* ---------------- draw ---------------- */
function drawBg() {
  const g = ctx.createLinearGradient(0, 0, 0, H);
  g.addColorStop(0, '#0b0e16');
  g.addColorStop(0.45, '#12151f');
  g.addColorStop(1, '#08090e');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);

  // moon
  const mx = W * 0.18, my = H * 0.12, mr = Math.min(W, H) * 0.07;
  const mg = ctx.createRadialGradient(mx - mr * 0.2, my - mr * 0.2, mr * 0.1, mx, my, mr * 1.6);
  mg.addColorStop(0, 'rgba(255,250,230,0.95)');
  mg.addColorStop(0.55, 'rgba(230,220,190,0.55)');
  mg.addColorStop(1, 'rgba(230,220,190,0)');
  ctx.fillStyle = mg;
  ctx.beginPath(); ctx.arc(mx, my, mr * 1.6, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = '#f2ead2';
  ctx.beginPath(); ctx.arc(mx, my, mr, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = 'rgba(180,170,150,0.25)';
  ctx.beginPath(); ctx.arc(mx + mr * 0.25, my - mr * 0.1, mr * 0.22, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.arc(mx - mr * 0.3, my + mr * 0.2, mr * 0.14, 0, Math.PI * 2); ctx.fill();

  // stars / gold speckles
  for (const s of stars) {
    const a = s.a * (0.55 + 0.45 * Math.sin(mistPhase * 1.3 + s.tw));
    ctx.fillStyle = `rgba(255,220,160,${a})`;
    ctx.beginPath(); ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2); ctx.fill();
  }

  // ink splatters
  ctx.fillStyle = 'rgba(0,0,0,0.35)';
  for (let i = 0; i < 6; i++) {
    const x = ((i * 97) % 100) / 100 * W;
    const y = ((i * 53) % 100) / 100 * H * 0.5;
    ctx.beginPath();
    ctx.ellipse(x, y, 18 + (i % 3) * 10, 8 + (i % 2) * 6, i, 0, Math.PI * 2);
    ctx.fill();
  }

  // mountains
  for (const m of mountains) {
    ctx.beginPath();
    ctx.moveTo(0, H);
    for (const p of m.pts) ctx.lineTo(p.x, p.y);
    ctx.lineTo(W, H);
    ctx.closePath();
    ctx.fillStyle = `rgba(8,10,14,${m.alpha})`;
    ctx.fill();
    ctx.strokeStyle = `rgba(40,45,55,${m.alpha * 0.6})`;
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    for (let i = 0; i < m.pts.length; i++) {
      const p = m.pts[i];
      if (i === 0) ctx.moveTo(p.x, p.y); else ctx.lineTo(p.x, p.y);
    }
    ctx.stroke();
  }

  // mist band
  const mistY = H * 0.72;
  const mist = ctx.createLinearGradient(0, mistY - 40, 0, mistY + 80);
  mist.addColorStop(0, 'rgba(120,130,150,0)');
  mist.addColorStop(0.4, `rgba(140,150,170,${0.08 + 0.04 * Math.sin(mistPhase)})`);
  mist.addColorStop(1, 'rgba(100,110,130,0)');
  ctx.fillStyle = mist;
  ctx.fillRect(0, mistY - 40, W, 140);
}

function drawRing() {
  const t = FEEL.ringThickness * (R / 160);
  // outer glow
  ctx.save();
  ctx.shadowColor = 'rgba(200,60,40,0.45)';
  ctx.shadowBlur = 28;
  ctx.beginPath();
  ctx.arc(CX, CY, R, 0, Math.PI * 2);
  ctx.arc(CX, CY, R - t, 0, Math.PI * 2, true);
  const rg = ctx.createLinearGradient(CX - R, CY - R, CX + R, CY + R);
  rg.addColorStop(0, '#c9a227');
  rg.addColorStop(0.35, '#8b1e18');
  rg.addColorStop(0.65, '#d4af37');
  rg.addColorStop(1, '#5a120e');
  ctx.fillStyle = rg;
  ctx.fill();
  ctx.restore();

  // brush texture arcs
  ctx.save();
  ctx.strokeStyle = 'rgba(255,210,120,0.25)';
  ctx.lineWidth = 2;
  for (let i = 0; i < 8; i++) {
    const a0 = i * 0.85 + mistPhase * 0.05;
    ctx.beginPath();
    ctx.arc(CX, CY, R - t * 0.35, a0, a0 + 0.35);
    ctx.stroke();
  }
  ctx.restore();

  // inner dark disc with faint clouds
  ctx.beginPath();
  ctx.arc(CX, CY, R - t - 1, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(6,8,14,0.55)';
  ctx.fill();

  // xiangyun faint
  ctx.strokeStyle = 'rgba(200,160,80,0.08)';
  ctx.lineWidth = 1.5;
  for (let i = 0; i < 3; i++) {
    const a = mistPhase * 0.15 + i * 2.1;
    const rr = (R - t) * (0.35 + i * 0.12);
    ctx.beginPath();
    ctx.arc(CX + Math.cos(a) * 10, CY + Math.sin(a * 0.7) * 8, rr * 0.4, 0, Math.PI * 1.4);
    ctx.stroke();
  }
}

function drawScore() {
  ctx.save();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `600 ${Math.floor(R * 0.42)}px "Songti SC","Noto Serif SC",serif`;
  ctx.fillStyle = 'rgba(255,230,180,0.18)';
  ctx.fillText(String(G.score), CX, CY);
  if (G.streak >= 2) {
    ctx.font = `500 ${Math.floor(R * 0.09)}px "Songti SC","Noto Serif SC",serif`;
    ctx.fillStyle = 'rgba(220,60,50,0.75)';
    ctx.fillText(`逆转 · ${G.streak}`, CX, CY + R * 0.28);
  }
  ctx.restore();
}

function drawOrb(o) {
  const wx = WUXING[o.kind];
  const ox = CX + Math.cos(o.angle) * o.radius;
  const oy = CY + Math.sin(o.angle) * o.radius;
  const or = FEEL.orbRadius * (R / 160) * (1 + 0.04 * Math.sin(o.pulse));
  ctx.save();
  ctx.shadowColor = wx.glow;
  ctx.shadowBlur = 18;
  const g = ctx.createRadialGradient(ox - or * 0.3, oy - or * 0.3, or * 0.1, ox, oy, or);
  g.addColorStop(0, '#fff');
  g.addColorStop(0.25, wx.fill);
  g.addColorStop(1, wx.rim);
  ctx.fillStyle = g;
  ctx.beginPath(); ctx.arc(ox, oy, or, 0, Math.PI * 2); ctx.fill();
  ctx.shadowBlur = 0;
  ctx.strokeStyle = 'rgba(255,255,255,0.35)';
  ctx.lineWidth = 1.5;
  ctx.stroke();
  ctx.fillStyle = 'rgba(10,12,18,0.82)';
  ctx.font = `700 ${Math.floor(or * 0.95)}px "Songti SC","Noto Serif SC",serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(wx.char, ox, oy + 1);
  if (o.hitFlash > 0) {
    ctx.fillStyle = `rgba(255,255,255,${o.hitFlash})`;
    ctx.beginPath(); ctx.arc(ox, oy, or, 0, Math.PI * 2); ctx.fill();
  }
  ctx.restore();
}

function drawPlayer() {
  const p = playerPos();
  // facing inward
  const ang = Math.atan2(CY - p.y, CX - p.x);
  const s = FEEL.playerSize * (R / 160);
  ctx.save();
  ctx.translate(p.x, p.y);
  ctx.rotate(ang);
  ctx.shadowColor = '#ffd76a';
  ctx.shadowBlur = 16;
  ctx.beginPath();
  ctx.moveTo(s * 1.1, 0);
  ctx.lineTo(-s * 0.7, s * 0.65);
  ctx.lineTo(-s * 0.35, 0);
  ctx.lineTo(-s * 0.7, -s * 0.65);
  ctx.closePath();
  const pg = ctx.createLinearGradient(-s, 0, s, 0);
  pg.addColorStop(0, '#8b5a00');
  pg.addColorStop(0.5, '#ffd76a');
  pg.addColorStop(1, '#fff3c0');
  ctx.fillStyle = pg;
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.strokeStyle = 'rgba(255,240,180,0.7)';
  ctx.lineWidth = 1;
  ctx.stroke();
  // trail
  ctx.globalAlpha = 0.35;
  ctx.beginPath();
  ctx.moveTo(-s * 0.9, 0);
  ctx.lineTo(-s * 2.2, s * 0.25);
  ctx.lineTo(-s * 2.2, -s * 0.25);
  ctx.closePath();
  ctx.fillStyle = '#ffc84a';
  ctx.fill();
  ctx.restore();
}

function drawBullets() {
  for (const b of G.bullets) {
    ctx.save();
    if (b.hostile) {
      ctx.shadowColor = '#ff5533';
      ctx.shadowBlur = 12;
      ctx.fillStyle = '#ff6644';
    } else {
      ctx.shadowColor = '#ffe08a';
      ctx.shadowBlur = 10;
      ctx.fillStyle = '#ffe9a8';
    }
    ctx.beginPath();
    ctx.arc(b.x, b.y, b.hostile ? 5.5 : 4.2, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
}

function drawParticles() {
  for (const p of particles) {
    const a = 1 - p.t / p.life;
    ctx.fillStyle = p.color;
    ctx.globalAlpha = a;
    ctx.beginPath(); ctx.arc(p.x, p.y, p.r * a, 0, Math.PI * 2); ctx.fill();
  }
  ctx.globalAlpha = 1;
}

function drawFloats() {
  for (const f of floats) {
    const a = 1 - f.t / f.life;
    ctx.save();
    ctx.globalAlpha = a;
    ctx.fillStyle = f.color;
    ctx.font = `700 ${Math.floor(18 + 6 * a)}px "Songti SC","Noto Serif SC",serif`;
    ctx.textAlign = 'center';
    ctx.fillText(f.text, f.x, f.y);
    ctx.restore();
  }
}

function drawTitle() {
  ctx.save();
  ctx.textAlign = 'center';
  ctx.fillStyle = 'rgba(240,210,140,0.55)';
  ctx.font = `500 ${Math.floor(14)}px "Songti SC","Noto Serif SC",serif`;
  ctx.fillText('五行环', CX, Math.max(28, CY - R - 36));
  ctx.restore();
}

function frame(now) {
  const dt = Math.min(0.033, (now - lastT) / 1000 || 0.016);
  lastT = now;
  update(dt);

  let ox = 0, oy = 0;
  if (shakeT > 0) {
    const k = shakeT / (FEEL.hitShakeMs / 1000);
    ox = (Math.random() - 0.5) * FEEL.hitShakePx * k * 2;
    oy = (Math.random() - 0.5) * FEEL.hitShakePx * k * 2;
  }
  ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
  ctx.clearRect(0, 0, W, H);
  ctx.save();
  ctx.translate(ox, oy);
  drawBg();
  drawRing();
  drawScore();
  for (const o of G.orbs) drawOrb(o);
  drawBullets();
  drawPlayer();
  drawParticles();
  drawFloats();
  drawTitle();
  ctx.restore();

  requestAnimationFrame(frame);
}

/* ---------------- input ---------------- */
let dragging = false;
let lastPointerAngle = null;
let pointerId = null;

function angleAt(clientX, clientY) {
  const rect = canvas.getBoundingClientRect();
  const x = clientX - rect.left;
  const y = clientY - rect.top;
  return Math.atan2(y - CY, x - CX);
}

function onDown(e) {
  Sfx.unlock();
  if (G.phase !== 'playing') return;
  const ev = e.changedTouches ? e.changedTouches[0] : e;
  dragging = true;
  pointerId = ev.pointerId ?? 't';
  lastPointerAngle = angleAt(ev.clientX, ev.clientY);
  // snap player toward pointer angle (smooth)
  // also treat short tap as shoot — tracked on up
  canvas._downAt = performance.now();
  canvas._downX = ev.clientX;
  canvas._downY = ev.clientY;
  e.preventDefault?.();
}

function onMove(e) {
  if (!dragging || G.phase !== 'playing') return;
  const ev = e.changedTouches ? e.changedTouches[0] : e;
  const a = angleAt(ev.clientX, ev.clientY);
  if (lastPointerAngle != null) {
    let da = a - lastPointerAngle;
    while (da > Math.PI) da -= Math.PI * 2;
    while (da < -Math.PI) da += Math.PI * 2;
    G.angle += da;
  }
  lastPointerAngle = a;
  // also allow absolute aim: blend toward pointer angle
  let diff = a - G.angle;
  while (diff > Math.PI) diff -= Math.PI * 2;
  while (diff < -Math.PI) diff += Math.PI * 2;
  G.angle += diff * 0.35;
  e.preventDefault?.();
}

function onUp(e) {
  if (!dragging) return;
  const ev = e.changedTouches ? e.changedTouches[0] : e;
  const dt = performance.now() - (canvas._downAt || 0);
  const dx = ev.clientX - (canvas._downX || 0);
  const dy = ev.clientY - (canvas._downY || 0);
  dragging = false;
  lastPointerAngle = null;
  // tap = shoot (small movement + short duration)
  if (dt < 280 && Math.hypot(dx, dy) < 14) {
    shoot();
  }
  e.preventDefault?.();
}

canvas.addEventListener('pointerdown', onDown, { passive: false });
canvas.addEventListener('pointermove', onMove, { passive: false });
window.addEventListener('pointerup', onUp, { passive: false });
canvas.addEventListener('touchstart', onDown, { passive: false });
canvas.addEventListener('touchmove', onMove, { passive: false });
canvas.addEventListener('touchend', onUp, { passive: false });

window.addEventListener('keydown', (e) => {
  Sfx.unlock();
  if (e.code === 'Space' || e.code === 'KeyJ') { e.preventDefault(); shoot(); }
  if (e.code === 'ArrowLeft' || e.code === 'KeyA') G.angle -= 0.12;
  if (e.code === 'ArrowRight' || e.code === 'KeyD') G.angle += 0.12;
});

retryBtn.addEventListener('click', () => { Sfx.unlock(); reset(); });

window.addEventListener('resize', resize);

/* ---------------- boot + smoke API ---------------- */
resize();
reset();
ready = true;
requestAnimationFrame((t) => { lastT = t; requestAnimationFrame(frame); });

window.__WUXING_RING_READY__ = true;
window.__WUXING_RING__ = {
  get phase() { return G.phase; },
  get score() { return G.score; },
  get streak() { return G.streak; },
  get angle() { return G.angle; },
  setAngle(a) { G.angle = a; },
  get orbCount() { return G.orbs.length; },
  get bulletCount() { return G.bullets.length; },
  get hostileCount() { return G.bullets.filter((b) => b.hostile).length; },
  get layout() { return { W, H, CX, CY, R, dpr: DPR }; },
  playerScreen() {
    const p = playerPos();
    return { x: p.x, y: p.y, angle: G.angle };
  },
  orbsScreen() {
    return G.orbs.map((o) => ({
      x: CX + Math.cos(o.angle) * o.radius,
      y: CY + Math.sin(o.angle) * o.radius,
      kind: o.kind,
      char: WUXING[o.kind].char,
      radius: FEEL.orbRadius * (R / 160),
    }));
  },
  shoot,
  reset,
  kill,
  /** Align player to face a given orb index (radial aim). */
  aimAtOrb(i = 0) {
    const o = G.orbs[i];
    if (!o) return false;
    G.angle = o.angle;
    return true;
  },
  /** Force-spawn one orb of each kind for visibility smoke. */
  wipeOrbs() { G.orbs.length = 0; return true; },
  ensureFiveElements() {
    const have = new Set(G.orbs.map((o) => o.kind));
    for (let k = 0; k < 5; k++) {
      if (!have.has(k)) {
        const o = randOrb();
        o.kind = k;
        o.angle = -Math.PI / 2 + (k / 5) * Math.PI * 2;
        o.radius = R * 0.55;
        G.orbs.push(o);
      }
    }
    return G.orbs.map((o) => WUXING[o.kind].char);
  },
  samplePixels() {
    // sample a few pixels for non-black / ring presence
    const img = ctx.getImageData;
    // use raw canvas buffer via temporary read
    const w = canvas.width, h = canvas.height;
    const data = ctx.getImageData(0, 0, w, h).data;
    const at = (x, y) => {
      const i = (Math.floor(y * DPR) * w + Math.floor(x * DPR)) * 4;
      return [data[i], data[i + 1], data[i + 2], data[i + 3]];
    };
    return {
      center: at(CX, CY),
      ring: at(CX + R - 4, CY),
      moon: at(W * 0.18, H * 0.12),
      player: (() => { const p = playerPos(); return at(p.x, p.y); })(),
    };
  },
};
